// ---------------------------------------------------------------------------
// Logistics: shipments of project materials from (mostly local) suppliers to the site or the warehouse,
// with delay intelligence. A shipment carries material lines of one or more projects; delivering it
// updates the materials' delivered quantity and status.
// ---------------------------------------------------------------------------
import { z } from 'zod';

export const SHIPMENT_STATUSES = [
  { value: 'ordered', label: 'Ordered' },
  { value: 'ready', label: 'Ready at supplier' },
  { value: 'dispatched', label: 'Out for delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number]['value'];
export const shipmentStatusLabel = (s: string) => SHIPMENT_STATUSES.find((x) => x.value === s)?.label ?? s;
export const OPEN_SHIPMENT: ShipmentStatus[] = ['ordered', 'ready', 'dispatched'];

export const SHIPMENT_DESTINATIONS = [
  { value: 'site', label: 'Project site (customer)' }, { value: 'customer_warehouse', label: 'Customer’s warehouse' }, { value: 'warehouse', label: 'Our warehouse' },
] as const;
export const destinationLabel = (d: string) => SHIPMENT_DESTINATIONS.find((x) => x.value === d)?.label ?? d;
/** Delivered to the customer (their site or their warehouse): counts as delivered. Our warehouse is our stock. */
export const toCustomer = (d: string) => d !== 'warehouse';
/** Supplier = a purchase (counts as ordered); warehouse = moving our own stock to the customer (not a new order). */
export const SHIPMENT_SOURCES = [{ value: 'supplier', label: 'From a supplier' }, { value: 'warehouse', label: 'From our warehouse to the customer' }] as const;
export const WAREHOUSE_SUPPLIER = 'Our warehouse';

const date = z.preprocess((v) => (v === '' ? null : v), z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').nullable()).optional();
const text = (max: number) => z.preprocess((v) => (v === '' ? null : v), z.string().trim().max(max).nullable()).optional();

export const shipmentSchema = z.object({
  supplier: z.string().trim().min(1, 'Give the supplier').max(200),
  supplierPo: text(100),
  source: z.enum(['supplier', 'warehouse']).default('supplier'),
  destination: z.enum(['site', 'customer_warehouse', 'warehouse']).default('site'),
  /** Our delivery note to the customer, when the supplier delivers directly to the site on it. */
  ourDeliveryNoteNo: text(100),
  deliveryAddress: text(300),
  orderDate: date,
  /** The date the supplier promised when ordering (kept to measure the supplier's reliability). */
  promisedDate: date,
  /** The current expected delivery date (moves when the supplier gives a new date). */
  expectedDate: date,
  /** When the site needs the materials. */
  neededBy: date,
  notes: text(2000),
  lines: z.array(z.object({ materialId: z.string().uuid(), qty: z.number().positive().max(1e9) })).max(200).default([]),
  /** Ask Accounts for our delivery note to the customer straight away (site / customer warehouse, no DN number yet). */
  requestDn: z.boolean().default(false),
}).strict();

export const shipmentUpdateSchema = z.object({
  supplier: z.string().trim().min(1).max(200).optional(),
  supplierPo: text(100),
  destination: z.enum(['site', 'customer_warehouse', 'warehouse']).optional(),
  ourDeliveryNoteNo: text(100),
  deliveryAddress: text(300),
  orderDate: date,
  promisedDate: date,
  expectedDate: date,
  neededBy: date,
  notes: text(2000),
  /** Why the expected date changed (asked when it moves later). */
  reason: text(500),
}).strict();

export const shipmentStatusSchema = z.object({
  status: z.enum(['ordered', 'ready', 'dispatched', 'delivered', 'cancelled']),
  note: text(500),
  /**
   * Delivery: the date, the supplier's delivery note, our delivery note to the customer, who received it, and the
   * quantity actually counted on arrival for every line (required: never assumed from the delivery note).
   */
  deliveredDate: date,
  deliveryNoteNo: text(100),
  ourDeliveryNoteNo: text(100),
  receivedBy: text(120),
  received: z.array(z.object({ lineId: z.string().uuid(), qty: z.number().min(0).max(1e9) })).max(200).optional(),
  /** Dispatch although the site isn't confirmed ready (recorded in the history with the note). */
  force: z.boolean().optional(),
}).strict();

/**
 * A line received short: the supplier sends the balance (a follow-up shipment is created), or it is closed with
 * the reason (e.g. credit note, customer accepted less); separately, our delivery note to the customer was corrected.
 */
export const shortageSchema = z.object({
  action: z.enum(['balance', 'close', 'customer_note_corrected']),
  note: text(500),
  expectedDate: date,
}).strict();
export const SHORT_STATUSES = [
  { value: 'open', label: 'Balance owed by the supplier' }, { value: 'balance_ordered', label: 'Balance on a follow-up shipment' }, { value: 'closed', label: 'Closed' },
] as const;

export interface SupplierStats {
  supplier: string; deliveries: number; onTime: number; onTimePct: number | null; avgDelayDays: number | null; open: number;
  /** Deliveries where less arrived than the shipment (and its delivery note) said. */
  shortDeliveries?: number;
}

export interface ShipmentWarning { key: string; level: 'red' | 'yellow'; text: string; action: string }
export interface ShipmentIntel { health: 'green' | 'yellow' | 'red'; predictedDate: string | null; daysLate: number | null; warnings: ShipmentWarning[] }

export interface ShipmentIntelInput {
  status: string; today: string; expectedDate: string | null; promisedDate: string | null; neededBy: string | null;
  /** Days since the last update (status, date or note). */
  daysSinceUpdate: number; etaSlips: number; supplier: SupplierStats | null;
}

const DAY = 86400_000;
const days = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY);
const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

/**
 * Delay intelligence for one shipment (pure: the same on the server and in tests). The predicted date is
 * the expected date plus the supplier's usual delay (from at least 3 past deliveries).
 */
export function shipmentIntel(i: ShipmentIntelInput): ShipmentIntel {
  if (!OPEN_SHIPMENT.includes(i.status as ShipmentStatus)) return { health: 'green', predictedDate: null, daysLate: null, warnings: [] };
  const w: ShipmentWarning[] = [];
  const s = i.supplier;
  const usualDelay = s && s.deliveries >= 3 && s.avgDelayDays && s.avgDelayDays > 0 ? Math.round(s.avgDelayDays) : 0;
  const predicted = i.expectedDate ? addDays(i.expectedDate, usualDelay) : null;
  const late = i.expectedDate && i.expectedDate < i.today ? days(i.expectedDate, i.today) : null;

  if (!i.expectedDate) {
    w.push({ key: 'no_date', level: 'yellow', text: 'No delivery date from the supplier.', action: 'Ask the supplier for a delivery date and record it.' });
  }
  if (late) {
    w.push({ key: 'late', level: 'red', text: `${plural(late, 'day')} late (expected ${i.expectedDate}).`, action: 'Call the supplier today, agree a new date and tell the project manager.' });
  }
  if (i.neededBy && i.expectedDate) {
    if (i.expectedDate > i.neededBy) {
      w.push({ key: 'misses_need', level: 'red', text: `Arrives ${plural(days(i.neededBy, i.expectedDate), 'day')} after the site needs it (${i.neededBy}).`,
        action: 'Push the supplier for an earlier date, find stock elsewhere, or re-plan the site work with the project manager.' });
    } else if (predicted && predicted > i.neededBy) {
      w.push({ key: 'may_miss_need', level: 'yellow', text: `This supplier is usually ${plural(usualDelay, 'day')} late, so it may arrive after the site needs it (${i.neededBy}).`,
        action: 'Confirm the date with the supplier now and agree a back-up.' });
    } else if (!late && days(i.expectedDate, i.neededBy) <= 2) {
      w.push({ key: 'tight', level: 'yellow', text: `Only ${plural(days(i.expectedDate, i.neededBy), 'day')} of margin before the site needs it.`, action: 'Confirm the delivery with the supplier a day before.' });
    }
  }
  if (i.etaSlips >= 2) {
    w.push({ key: 'slipping', level: 'yellow', text: `The delivery date moved later ${i.etaSlips} times.`, action: 'Ask for a firm date in writing; consider a second supplier.' });
  }
  if (i.status === 'ordered' && i.expectedDate && !late && days(i.today, i.expectedDate) <= 2) {
    w.push({ key: 'not_ready', level: 'yellow', text: `Due in ${plural(days(i.today, i.expectedDate), 'day')} but not marked ready at the supplier.`, action: 'Check it is ready and book the delivery.' });
  }
  if (i.daysSinceUpdate >= 7 && (!i.expectedDate || days(i.today, i.expectedDate) <= 7)) {
    w.push({ key: 'no_update', level: 'yellow', text: `No update for ${plural(i.daysSinceUpdate, 'day')}.`, action: 'Ask the supplier for the status and record it.' });
  }
  if (s && s.deliveries >= 3 && s.onTimePct != null && s.onTimePct < 70) {
    w.push({ key: 'unreliable_supplier', level: 'yellow', text: `${s.supplier} delivers on time only ${s.onTimePct}% of the time${s.avgDelayDays ? ` (${Math.round(s.avgDelayDays)} days late on average)` : ''}.`,
      action: 'Follow this order closely; prefer more reliable suppliers for urgent items.' });
  }
  if (s && (s.shortDeliveries ?? 0) >= 2) {
    w.push({ key: 'short_deliverer', level: 'yellow', text: `${s.supplier} delivered less than the delivery note ${s.shortDeliveries} times.`,
      action: 'Have the receiver count every line on arrival before signing, and send our delivery note to the customer only for what arrived.' });
  }
  const health = w.some((x) => x.level === 'red') ? 'red' : w.length ? 'yellow' : 'green';
  return { health, predictedDate: predicted, daysLate: late, warnings: w };
}

export interface ShipmentLine {
  id: string; materialId: string | null; projectId: string | null; projectNumber: string | null; projectName: string | null;
  description: string; system: string | null; unit: string | null; qty: number; qtyReceived: number | null;
  /** Received short: how it is being resolved (null = not short). */
  shortStatus: 'open' | 'balance_ordered' | 'closed' | null; shortNote: string | null;
  balanceShipmentId: string | null; balanceShipmentNumber: string | null; customerNoteCorrected: boolean;
}
export interface ShipmentRow {
  id: string; number: string; supplier: string; supplierPo: string | null; status: ShipmentStatus; destination: string; deliveryAddress: string | null;
  source: 'supplier' | 'warehouse'; ourDeliveryNoteNo: string | null;
  /** Lines received short whose balance the supplier still owes. */
  openShortages: number;
  orderDate: string | null; promisedDate: string | null; expectedDate: string | null; neededBy: string | null; deliveredDate: string | null;
  projects: { id: string; number: string; name: string }[]; lineCount: number; notes: string | null; updatedAt: string; intel: ShipmentIntel;
  /** Site readiness for this delivery (separate from late / at-risk). */
  readiness: SiteReadiness;
  /** Our delivery note to the customer; null when the delivery is not to the customer (our warehouse). */
  dn: ShipmentDn | null;
}
export interface ShipmentEvent { kind: string; fromValue: string | null; toValue: string | null; reason: string | null; at: string; byName: string | null }
export interface ShipmentDocument {
  id: string; name: string; webUrl: string | null; projectNumber: string | null; createdAt: string; byName: string | null;
  /** 'signed_dn' = our delivery note signed by the customer; null = any other delivery paper (supplier DN, photo…). */
  kind: 'signed_dn' | null;
}

/** Our delivery note to the customer (site / customer warehouse deliveries): Logistics requests it, Accounts issues it
 *  in Odoo (Source Document = the shipment number), the CRM links it, Logistics returns the copy the customer signed. */
export const DN_STATUSES = [
  { value: 'not_requested', label: 'Not requested' },
  { value: 'requested', label: 'Requested' },
  { value: 'ready', label: 'Ready' },
  { value: 'signed', label: 'Signed' },
] as const;
export type DnStatus = (typeof DN_STATUSES)[number]['value'];
export interface ShipmentDn {
  status: DnStatus;
  requestedAt: string | null; requestedByName: string | null;
  /** The Odoo delivery order (stock.picking) once linked. */
  odooPickingId: number | null; odooName: string | null; odooState: string | null; issuedAt: string | null;
  /** A PDF of the delivery note is attached to it in Odoo (else the CRM prints it from Odoo's data). */
  odooPdf: boolean;
  signedDoc: { id: string; name: string; webUrl: string | null; at: string; byName: string | null } | null;
}
/** Link a shipment to its Odoo delivery order, or (Odoo not reachable) record the delivery note number typed by hand. */
export const dnLinkSchema = z.union([
  z.object({ pickingId: z.number().int().positive() }).strict(),
  z.object({ manualNo: z.string().trim().min(1).max(100) }).strict(),
]);
/** Accounts → Delivery notes: one customer delivery and where its delivery note is. */
export interface DeliveryNoteRow {
  shipmentId: string; shipmentNumber: string; customer: string | null; projects: { id: string; number: string; name: string }[];
  destination: string; deliveryAddress: string | null; expectedDate: string | null; deliveredDate: string | null; lineCount: number;
  dn: ShipmentDn;
}
export interface ShipmentDetail extends ShipmentRow {
  lines: ShipmentLine[]; events: ShipmentEvent[]; deliveryNoteNo: string | null; receivedBy: string | null; createdByName: string | null;
  /** Delivery notes saved in the projects' Microsoft 365 folders (Danat Projects/…/04 Materials & Delivery). */
  documents: ShipmentDocument[];
  /** The site-readiness checks recorded for this delivery (per project). */
  readinessEntries: ReadinessEntry[];
  /** Receiving contact per project of this delivery (operational). */
  receiving: { projectId: string; number: string; location: string | null; contactName: string | null; contactPhone: string | null; instructions: string | null }[];
  people: { id: string; name: string }[];
}
export interface LogisticsList {
  shipments: ShipmentRow[]; suppliers: SupplierStats[]; canManage: boolean;
  counts: { open: number; late: number; atRisk: number; thisWeek: number; deliveredThisMonth: number };
  projects: { id: string; number: string; name: string }[];
}
export interface MaterialToShip {
  id: string; projectId: string; description: string; system: string | null; brand: string | null; unit: string | null; qty: number;
  /** Still to order from a supplier: required − delivered to site − in our warehouse − on open supplier shipments. */
  remaining: number;
  /** In our warehouse and not yet on an open warehouse-to-site shipment (what can be moved to site). */
  inWarehouse: number;
  supplier: string | null; status: string; requiredOnSite: string | null; criticality: string | null;
}

// ---------------------------------------------------------------------------
// Operational handover, material readiness and site readiness (logistics intelligence).
// ---------------------------------------------------------------------------

export const HANDOVER_STATUSES = [
  { value: 'not_started', label: 'Not handed over' }, { value: 'submitted', label: 'Waiting for logistics' },
  { value: 'accepted', label: 'Accepted by logistics' }, { value: 'returned', label: 'Returned: information missing' },
] as const;
export type HandoverStatus = (typeof HANDOVER_STATUSES)[number]['value'];

export const logisticsHandoverSchema = z.object({
  logisticsOwnerId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid().nullable()).optional(),
  deliveryLocation: text(300),
  receivingContactName: text(120),
  receivingContactPhone: text(40),
  deliveryInstructions: text(2000),
  outstanding: text(2000),
}).strict();

export interface LogisticsHandover {
  projectId: string; number: string; name: string; status: HandoverStatus; logisticsOwnerId: string | null; logisticsOwnerName: string | null;
  deliveryLocation: string | null; receivingContactName: string | null; receivingContactPhone: string | null; deliveryInstructions: string | null;
  outstanding: string | null; submittedAt: string | null; acceptedAt: string | null; acceptedByName: string | null;
  /** The customer's PO number and date only (never the PO document, its value or prices), to match deliveries. */
  customerPoNumber: string | null; customerPoDate: string | null;
  /** Operational information still missing before logistics can accept. */
  missing: string[]; canEdit: boolean; canSubmit: boolean; canAccept: boolean; owners: { id: string; name: string }[];
  /** Accepted earlier, but information has since been removed: shown as needing attention, not as complete. */
  acceptedIncomplete: boolean;
}

/** What the handover still lacks (it can't be confirmed while anything is missing). */
export function handoverMissing(h: { deliveryLocation: string | null; receivingContactName: string | null; receivingContactPhone: string | null; logisticsOwnerId: string | null }, materials: { total: number; withoutDate: number }): string[] {
  const m: string[] = [];
  if (!h.deliveryLocation) m.push('Delivery location');
  if (!h.receivingContactName || !h.receivingContactPhone) m.push('Receiving contact (name and phone)');
  // No logistics owner to pick: every Logistics user gets the handover and the project's shipments.
  // The items come from the BOQ; the date each is needed comes with the purchase request (no separate date to fill).
  if (!materials.total) m.push('Items to deliver (Project → BOQ)');
  return m;
}

// --- Site readiness -----------------------------------------------------------
export const SITE_READINESS_ITEMS = [
  { value: 'site_access', label: 'Site access confirmed' },
  { value: 'storage', label: 'Storage available' },
  { value: 'unloading', label: 'Unloading arrangements confirmed' },
  { value: 'receiving_contact', label: 'Receiving contact confirmed' },
  { value: 'delivery_window', label: 'Delivery window approved' },
] as const;
export type ReadinessItemKey = (typeof SITE_READINESS_ITEMS)[number]['value'];

export const readinessSchema = z.object({
  projectId: z.string().uuid(),
  item: z.enum(['site_access', 'storage', 'unloading', 'receiving_contact', 'delivery_window']),
  state: z.enum(['confirmed', 'blocked', 'pending']),
  responsibleId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid().nullable()).optional(),
  confirmedOn: date,
  blocker: text(500),
}).strict();

export interface ReadinessEntry { projectId: string; item: ReadinessItemKey; state: 'confirmed' | 'blocked' | 'pending'; responsibleId: string | null; responsibleName: string | null; confirmedOn: string | null; blocker: string | null }
export interface SiteReadiness { status: 'ready' | 'blocked' | 'incomplete' | 'not_required'; confirmed: number; total: number; blockers: string[]; missing: string[] }

/** The receiving checks that apply to a delivery: all five on site; at a warehouse (ours or the customer's), everything except site access. */
export const readinessItemsFor = (destination: string): { value: ReadinessItemKey; label: string }[] => (destination !== 'site'
  ? SITE_READINESS_ITEMS.filter((x) => x.value !== 'site_access').map((x) => ({
    ...x,
    label: x.value === 'storage' ? (destination === 'warehouse' ? 'Warehouse space available' : 'Customer’s warehouse ready to receive')
      : x.value === 'receiving_contact' ? (destination === 'warehouse' ? 'Storekeeper / receiver confirmed' : 'Customer’s storekeeper confirmed') : x.label,
  }))
  : [...SITE_READINESS_ITEMS]);

/**
 * A delivery's receiving readiness. Blocked when any applicable check is blocked; ready only when all of them are
 * confirmed (with a responsible person and date) and, for the site, the project has a receiving contact; otherwise
 * incomplete. Deliveries to our warehouse use the warehouse checks (no site access). Missing information never counts as confirmed.
 */
export function siteReadiness(destination: string, projects: { projectId: string; label: string; hasReceivingContact: boolean }[], entries: Pick<ReadinessEntry, 'projectId' | 'item' | 'state' | 'blocker' | 'responsibleId' | 'confirmedOn'>[]): SiteReadiness {
  const items = readinessItemsFor(destination);
  const site = destination === 'site';
  const total = items.length * Math.max(1, projects.length);
  if (!projects.length) return { status: 'incomplete', confirmed: 0, total, blockers: [], missing: ['No project materials on this delivery'] };
  let confirmed = 0;
  const blockers: string[] = [];
  const missing: string[] = [];
  const multi = projects.length > 1;
  for (const p of projects) {
    for (const it of items) {
      const e = entries.find((x) => x.projectId === p.projectId && x.item === it.value);
      const tag = multi ? ` (${p.label})` : '';
      if (e?.state === 'blocked') { blockers.push(`${it.label}${tag}: blocked — ${e.blocker ?? 'no reason given'}`); continue; }
      const ok = e?.state === 'confirmed' && !!e.responsibleId && !!e.confirmedOn && (!site || it.value !== 'receiving_contact' || p.hasReceivingContact);
      if (ok) confirmed++;
      else missing.push(site && it.value === 'receiving_contact' && !p.hasReceivingContact ?`Receiving contact not recorded on the project${tag}` : `${it.label}${tag}`);
    }
  }
  return { status: blockers.length ? 'blocked' : confirmed === total ? 'ready' : 'incomplete', confirmed, total, blockers, missing };
}

// --- Material readiness ---------------------------------------------------------
/** A material line with the shipment lines that carry it (each shipment line is counted once). */
export interface MaterialReadinessInput {
  id: string; projectId: string; system: string | null; description: string; unit: string | null;
  qty: number | null; qtyDelivered: number | null; status: string | null; criticality: string | null; requiredOnSite: string | null;
  /** Held in our warehouse (received there, not yet moved to site). */
  qtyInWarehouse?: number | null;
  /**
   * Its shipment lines: quantity on the shipment, quantity counted on arrival (delivered shipments), the expected /
   * likely arrival, where it comes from (default supplier) and goes (default site), and whether a short receipt's
   * balance is still owed by the supplier (default: when less was received than shipped).
   */
  lines: { shipmentStatus: string; qty: number; qtyReceived: number | null; arrival: string | null; source?: string; destination?: string; shortOpen?: boolean }[];
}
export interface MaterialReadinessRow {
  id: string; projectId: string; system: string | null; description: string; unit: string | null; criticality: string | null; requiredOnSite: string | null;
  /** null = Unknown (the source data is missing). */
  required: number | null; ordered: number | null; shipped: number | null; delivered: number | null; outstanding: number | null; notOrdered: number | null;
  /** Received at our warehouse (all receipts there), now in the warehouse, and still owed by suppliers after short receipts. */
  warehouseReceived: number; inWarehouse: number; supplierBalance: number;
  overOrdered: boolean; overDelivered: boolean;
  /** Critical material that could delay planned site work (why), or null. */
  criticalRisk: string | null;
  dataGaps: string[];
}

const ACTIVE = (s: string) => s !== 'cancelled';
const LEFT_SUPPLIER = (s: string) => s === 'dispatched' || s === 'delivered';
const ORDERED_STATUSES = ['ordered', 'shipped', 'delivered', 'installed'];
const SHIPPED_STATUSES = ['shipped', 'delivered', 'installed'];

type Line = MaterialReadinessInput['lines'][number];
const shortOpen = (l: Line) => l.shortOpen ?? (l.shipmentStatus === 'delivered' && l.qtyReceived != null && l.qtyReceived < l.qty);

/**
 * Required = the material's quantity. Ordered = what suppliers committed to on its non-cancelled supplier shipments
 * (each shipment line once; a material may be split over several shipments). A short receipt does not reduce Ordered
 * while the supplier still owes the balance (shown as Supplier balance); once the balance is put on a follow-up
 * shipment or closed, the short line counts what arrived, so the replacement is not counted twice. Moves from our
 * warehouse to the site are not orders. Shipped = supplier lines that left the supplier (out for delivery, or what
 * arrived). Warehouse received = what arrived at our warehouse. Delivered = the quantity delivered to site (site
 * receipts add to it once; a warehouse receipt does not). Outstanding = required − delivered to site. Not ordered =
 * required − ordered. When a material is marked ordered/shipped outside logistics (no shipment), the figure is Unknown.
 */
export function materialReadiness(m: MaterialReadinessInput, today: string, horizonDays = 14): MaterialReadinessRow {
  const active = m.lines.filter((l) => ACTIVE(l.shipmentStatus));
  const supplier = active.filter((l) => (l.source ?? 'supplier') === 'supplier');
  const status = m.status ?? 'to_order';
  const required = m.qty == null || m.qty < 0 ? null : m.qty;
  const arrived = (l: Line) => l.qtyReceived ?? l.qty;
  const orderedOf = (l: Line) => (l.shipmentStatus !== 'delivered' ? l.qty : shortOpen(l) ? Math.max(l.qty, arrived(l)) : arrived(l));
  const shippedOf = (l: Line) => (l.shipmentStatus === 'delivered' ? arrived(l) : l.qty);
  const sum = (ls: Line[], f: (l: Line) => number) => ls.reduce((a, l) => a + f(l), 0);
  const delivered = m.qtyDelivered != null ? m.qtyDelivered : ['delivered', 'installed'].includes(status) ? null : 0;
  // Without shipments: a line fully delivered was ordered and shipped at least that much; otherwise a line marked ordered is Unknown.
  const fullyIn = delivered != null && required != null && delivered >= required && delivered > 0;
  const ordered = supplier.length ? sum(supplier, orderedOf) : fullyIn ? delivered : ORDERED_STATUSES.includes(status) ? null : 0;
  const shipped = supplier.length ? sum(supplier.filter((l) => LEFT_SUPPLIER(l.shipmentStatus)), shippedOf) : fullyIn ? delivered : SHIPPED_STATUSES.includes(status) ? null : 0;
  const warehouseReceived = sum(supplier.filter((l) => l.shipmentStatus === 'delivered' && l.destination === 'warehouse'), arrived);
  const supplierBalance = sum(supplier.filter((l) => l.shipmentStatus === 'delivered' && shortOpen(l)), (l) => Math.max(0, l.qty - arrived(l)));
  const outstanding = required == null || delivered == null ? null : Math.max(0, required - delivered);
  const notOrdered = required == null || ordered == null ? null : Math.max(0, required - ordered);
  const gaps: string[] = [];
  if (required == null) gaps.push('No required quantity');
  if (!m.requiredOnSite && outstanding !== 0) gaps.push('No required-on-site date');
  if (ordered == null) gaps.push('Marked ordered without a shipment (supplier commitment unknown)');
  if (delivered == null) gaps.push('Marked delivered without a delivered quantity');  let risk: string | null = null;
  if (m.criticality === 'critical' && outstanding !== 0) {
    const open = active.filter((l) => l.shipmentStatus !== 'delivered');
    const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400_000);
    const latest = open.some((l) => !l.arrival) ? null : open.map((l) => l.arrival!).sort().pop() ?? null;
    if (!m.requiredOnSite) risk = 'Critical material without a required-on-site date';
    else if (m.requiredOnSite < today) risk = `Required on site ${dayDiff(m.requiredOnSite, today)} days ago and not fully delivered`;
    else if (notOrdered && dayDiff(today, m.requiredOnSite) <= horizonDays) risk = `${notOrdered} ${m.unit ?? ''} not on any shipment, needed on site ${m.requiredOnSite}`.replace('  ', ' ');
    else if (notOrdered == null || outstanding == null) risk = 'Critical material with unknown quantities';
    else if (open.length && open.some((l) => !l.arrival)) risk = 'A shipment for it has no delivery date';
    else if (latest && latest > m.requiredOnSite) risk = `Arrives ${latest}, after it is needed on site (${m.requiredOnSite})`;
  }
  return {
    id: m.id, projectId: m.projectId, system: m.system, description: m.description, unit: m.unit, criticality: m.criticality, requiredOnSite: m.requiredOnSite,
    required, ordered, shipped, delivered, outstanding, notOrdered,
    warehouseReceived, inWarehouse: m.qtyInWarehouse ?? 0, supplierBalance,
    overOrdered: required != null && ordered != null && ordered > required, overDelivered: required != null && delivered != null && delivered > required,
    criticalRisk: risk, dataGaps: gaps,
  };
}

export interface ReadinessSystemSummary { projectId: string; system: string; lines: number; delivered: number; partly: number; shortages: number; supplierShort: number; unknown: number; criticalAtRisk: number }
export interface MaterialReadinessReport {
  projects: { id: string; number: string; name: string; handoverStatus: HandoverStatus; plannedStart: string | null }[];
  systems: ReadinessSystemSummary[];
  materials: MaterialReadinessRow[];
}

/** Per project and system: material lines, fully delivered, partly delivered, with a shortage (not ordered), unknown, critical at risk. */
export function summarizeReadiness(rows: MaterialReadinessRow[]): ReadinessSystemSummary[] {
  const map = new Map<string, ReadinessSystemSummary>();
  for (const r of rows) {
    const key = `${r.projectId}|${r.system ?? 'other'}`;
    const s = map.get(key) ?? { projectId: r.projectId, system: r.system ?? 'other', lines: 0, delivered: 0, partly: 0, shortages: 0, supplierShort: 0, unknown: 0, criticalAtRisk: 0 };
    s.lines++;
    if (r.outstanding === 0) s.delivered++;
    else if (r.delivered && r.delivered > 0) s.partly++;
    if (r.notOrdered) s.shortages++;
    if (r.supplierBalance) s.supplierShort++;
    if (r.required == null || r.ordered == null || r.delivered == null) s.unknown++;
    if (r.criticalRisk) s.criticalAtRisk++;
    map.set(key, s);
  }
  return [...map.values()];
}

// --- Data completeness (kept apart from late / at-risk) -------------------------
export interface CompletenessItem {
  kind: 'handover' | 'no_materials' | 'no_required_date' | 'no_supplier_date' | 'delivery_unconfirmed' | 'short_delivery' | 'customer_note' | 'signed_dn';
  text: string; projectId: string | null; shipmentId: string | null; label: string;
}
export interface LogisticsCompleteness { items: CompletenessItem[]; counts: Record<CompletenessItem['kind'], number> }